// The "nudge the opponent" button of an online game: a DOM button in the
// corner that appears once the opponent has sat on their move for a while
// (`AfkPingTracker` decides when, and enforces the cooldown the server also
// applies).
import { AfkPingTracker } from '@bn-games/room-client';
import { PingButton } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

const INSET_REM = '1rem';
// Above the canvas, below ui-shell's modal overlay.
const Z_INDEX = '50';

export interface OnlinePingDeps {
  readonly isEnabled: () => boolean;
  readonly send: () => void;
}

export class OnlinePing {
  private readonly tracker = new AfkPingTracker();
  private readonly container: HTMLElement;
  private readonly button: PingButton;
  private readonly unsubscribe: () => void;
  private waiting = false;

  constructor(private readonly deps: OnlinePingDeps) {
    this.container = document.createElement('div');
    this.container.style.position = 'fixed';
    this.container.style.right = INSET_REM;
    this.container.style.bottom = INSET_REM;
    this.container.style.zIndex = Z_INDEX;
    document.body.appendChild(this.container);
    this.button = new PingButton({ label: '🔔', title: t('game.pingOpponent'), ariaLabel: t('game.pingOpponent') });
    this.button.mount(this.container);
    this.button.setOnClick(() => {
      this.deps.send();
      this.tracker.markPingSent();
      this.refresh();
    });
    this.unsubscribe = this.tracker.subscribe(() => this.refresh());
  }

  /** Tell the tracker whether we are waiting on the opponent's move right now. */
  setWaiting(waiting: boolean): void {
    // The tracker restarts its idle count on every call, so only report edges.
    if (waiting !== this.waiting) {
      this.waiting = waiting;
      this.tracker.setOpponentTurn(waiting);
    }
    this.refresh();
  }

  dispose(): void {
    this.unsubscribe();
    this.tracker.dispose();
    this.button.dispose();
    this.container.remove();
  }

  private refresh(): void {
    this.button.setVisible(this.tracker.canPing() && this.deps.isEnabled());
  }
}
